import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, clearAuthError, loginUser, RootState } from 'data';
import { useNavigate } from 'react-router-dom';
import {
  AuthErrorMessage,
  Button,
  InputField,
  LanguageSwitcher,
  showEmailVerifiedAlert,
} from 'components';
import { BlueLogo } from 'icons';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthErrorCode } from 'helpers';

export const Login: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
	const [errorEmail, setErrorEmail] = useState('');
	const [searchParams] = useSearchParams();
	const verifyToken = searchParams.get('verify');

	useEffect(() => {
		dispatch(clearAuthError());
	}, [dispatch]);

	useEffect(() => {
		if (verifyToken) {
			showEmailVerifiedAlert();
		}
	}, [verifyToken]);

  	const { loginLoading } = useSelector((state: RootState) => state.auth);

	const showLoginError = (code: AuthErrorCode, email: string) => {
		setErrorCode(code);
		setErrorEmail(email);
	};

	const clearLoginError = () => {
		if (errorCode) setErrorCode(null);
	};

	const handleLogin = async () => {
		const email = username.trim();

		if (!email) {
			showLoginError('emailRequired', email);
			return;
		}
		if (!password) {
			showLoginError('passwordRequired', email);
			return;
		}

		setErrorCode(null);

		const result = await dispatch(loginUser({ email, password }));
		if (loginUser.rejected.match(result)) {
			showLoginError(result.payload ?? 'unknown', email);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') {
		handleLogin();
		}
	};

	if (loginLoading) {
		return (
			<div className="flex items-center justify-center h-svh w-full bg-[#F5F7F9]">
				<div className="w-10 h-10 border-4 border-gray-300 border-t-[#FF6F61] rounded-full animate-spin"></div>
			</div>
		);
	}

	return (
		<div
			className="relative flex items-center justify-center min-h-svh bg-[#F5F7F9] flex-col space-y-[24px] px-[16px] py-[32px] sm:py-[64px]"
			onKeyDown={handleKeyDown}
			tabIndex={0}
		>
			<LanguageSwitcher className="absolute top-[16px] right-[16px] sm:top-[24px] sm:right-[24px]" />
			<div>
				<BlueLogo width={122} height={33} />
			</div>
			<div className="bg-white border-[#D5D9DF] border p-[24px] sm:p-[32px] rounded-[24px] pb-[36px] w-full text-black flex flex-col items-center max-w-[440px] sm:max-w-[500px] space-y-[17px]">
				<div className="flex flex-col space-y-[4px]">
				<h2 className="text-[24px] leading-[29px] font-[500] text-center text-[#2E435F]">
					{t('login.title')}
				</h2>
				<h2 className="text-[14px] font-[400] text-center text-[#58697F]">
					{t('login.subtitle')}
				</h2>
				</div>
				<div className="space-y-[12px] w-full">
				<InputField
					label={t('login.username')}
					type="text"
					placeholder={t('login.username')}
					value={username}
					onChange={(e) => {
						setUsername(e.target.value);
						clearLoginError();
					}}
				/>
				<InputField
					label={t('login.password')}
					type="password"
					placeholder={t('login.password')}
					value={password}
					onChange={(e) => {
						setPassword(e.target.value);
						clearLoginError();
					}}
				/>
				</div>
				<div className="flex self-end">
					<p
						className="text-sm cursor-pointer text-[#2E435F] font-[600]"
						onClick={() => navigate('/forgot-password')}>
						{t('login.forgotPassword')}
					</p>
				</div>
				<Button
				label={loginLoading ? t('login.loggingIn') : t('login.continue')}
				onClick={handleLogin}
				loading={loginLoading}
				disabled={loginLoading}
				style={{ width: '100%' }}
				/>
				{errorCode && (
					<AuthErrorMessage code={errorCode} email={errorEmail} />
				)}
			</div>
			<p className="mt-4 text-sm text-[#58697F] font-[700]">
				{t('login.noAccount')}{' '}
				<span
				className="text-[#FF6F61] cursor-pointer hover:underline"
				onClick={() => navigate('/register')}
				>
				{t('login.signUp')}
				</span>
			</p>
		</div>
	);
};
